import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash, Play, CheckCircle, ChartLineUp, PencilSimple, MagnifyingGlass, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import SeasonSelector from "@/components/SeasonSelector";
import { logger } from "@/lib/logger";

const initialForm = { rival: "", competicion: "Liga", fecha: "", hora: "20:00", temporada: "", aforo_maximo: 1015 };

export default function Partidos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [filterTemporada, setFilterTemporada] = useState("");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // 'desc' = más recientes primero, 'asc' = más antiguos primero
  const isAdmin = can(user, "admin");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterTemporada) params.temporada = filterTemporada;
      const { data } = await api.get("/matches", { params });
      setMatches(data);
    } catch (e) { logger.warn("Load matches:", e?.message); }
  }, [filterTemporada]);

  useEffect(() => { load(); }, [load]);

  // Búsqueda + orden por fecha (togglable ASC/DESC).
  // La búsqueda actúa sobre: rival, competición y fecha (texto libre).
  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const norm = (s) => (s || "").toString().toLowerCase();
    const list = matches.filter((m) => {
      if (!q) return true;
      // Fecha en formato ISO (2026-06-12) y también dd/mm/yyyy (12/06/2026)
      const fechaIso = norm(m.fecha);
      const fechaEs = fechaIso.length >= 10
        ? `${fechaIso.slice(8, 10)}/${fechaIso.slice(5, 7)}/${fechaIso.slice(0, 4)}`
        : "";
      return (
        norm(m.rival).includes(q) ||
        norm(m.competicion).includes(q) ||
        fechaIso.includes(q) ||
        fechaEs.includes(q)
      );
    });
    // Orden: fecha + hora, según sortOrder
    return [...list].sort((a, b) => {
      const da = `${a.fecha || ""} ${a.hora || ""}`;
      const db = `${b.fecha || ""} ${b.hora || ""}`;
      return sortOrder === "desc" ? db.localeCompare(da) : da.localeCompare(db);
    });
  }, [matches, search, sortOrder]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/matches/${editing.id}`, form);
        toast.success("Partido actualizado");
      } else {
        await api.post("/matches", form);
        toast.success("Partido creado");
      }
      setShowForm(false); setEditing(null); setForm(initialForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (m) => {
    setEditing(m);
    setForm({ rival: m.rival, competicion: m.competicion, fecha: m.fecha, hora: m.hora, temporada: m.temporada, aforo_maximo: m.aforo_maximo });
    setShowForm(true);
  };

  const activate = async (id) => {
    try {
      await api.post(`/matches/${id}/activate`);
      toast.success("Partido activado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const finish = async (id) => {
    if (!window.confirm("¿Finalizar partido?")) return;
    try {
      await api.post(`/matches/${id}/finish`);
      toast.success("Partido finalizado");
      load();
    } catch (e) { toast.error("No se pudo finalizar"); logger.warn(e); }
  };

  const reopen = async (m) => {
    const ok = window.confirm(
      `¿Seguro que quieres reabrir este partido?\n\n` +
      `vs ${m.rival} (${m.fecha})\n\n` +
      `Este partido volverá a estar activo y podrás seguir registrando movimientos sobre él. ` +
      `Ningún dato se perderá.`
    );
    if (!ok) return;
    try {
      await api.post(`/matches/${m.id}/activate`);
      toast.success("Partido reabierto — ahora está activo");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo reabrir el partido");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar partido?")) return;
    try {
      await api.delete(`/matches/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const ESTADO = {
    pendiente: "text-slate-600 bg-zinc-500/10 border-zinc-500/30",
    activo: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30",
    finalizado: "text-slate-500 bg-zinc-700/30 border-zinc-700",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Módulo 3</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Partidos</h1>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-1">Temporada</div>
            <SeasonSelector value={filterTemporada} onChange={setFilterTemporada} includeAll size="md" testid="partidos-season-filter" />
          </div>
          {isAdmin && (
            <button
              data-testid="add-partido-btn"
              onClick={() => setShowForm(true)}
              className="bg-[#007AFF] hover:bg-[#005bb5] px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition"
              style={{ borderRadius: 6 }}
            >
              <Plus size={18} weight="bold" /> Nuevo Partido
            </button>
          )}
        </div>
      </div>

      {/* Buscador + orden */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="partidos-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar partido..."
            className="w-full bg-white border border-slate-200 pl-10 pr-3 h-11 text-slate-900 focus:border-[#2563EB] focus:bg-white outline-none"
            style={{ borderRadius: 8 }}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <button
          data-testid="partidos-sort-toggle"
          onClick={() => setSortOrder((s) => (s === "desc" ? "asc" : "desc"))}
          title={sortOrder === "desc" ? "Más recientes primero (pulsa para invertir)" : "Más antiguos primero (pulsa para invertir)"}
          className="h-11 px-4 flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-display font-bold uppercase tracking-wide text-xs active:scale-[0.98] transition"
          style={{ borderRadius: 8 }}
        >
          {sortOrder === "desc" ? <ArrowDown size={16} weight="bold" /> : <ArrowUp size={16} weight="bold" />}
          Fecha {sortOrder === "desc" ? "↓" : "↑"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMatches.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Sin partidos {search ? `que coincidan con "${search}"` : "registrados"}</div>}
        {filteredMatches.map((m) => (
          <div
            key={m.id}
            className={`p-5 border ${m.estado === "activo" ? "border-[#22C55E]/50 bg-[#22C55E]/5" : "border-slate-200 bg-white"}`}
            style={{ borderRadius: 6 }}
            data-testid={`partido-card-${m.id}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">{m.competicion}</div>
                <div className="font-display font-bold text-2xl uppercase mt-1">vs {m.rival}</div>
              </div>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${ESTADO[m.estado]}`} style={{ borderRadius: 4 }}>
                {m.estado}
              </span>
            </div>
            <div className="mt-3 text-sm text-slate-600">
              {m.fecha} • {m.hora}
            </div>
            <div className="text-xs text-slate-500">Aforo: {m.aforo_maximo} • {m.temporada}</div>
            {isAdmin && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => navigate(`/partidos/${m.id}`)}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-3 h-9 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                  style={{ borderRadius: 8 }}
                >
                  <ChartLineUp size={14} weight="bold" /> Ver dashboard
                </button>
                {m.estado === "pendiente" && (
                  <button
                    data-testid={`activate-partido-${m.id}`}
                    onClick={() => activate(m.id)}
                    className="bg-[#22C55E] hover:bg-[#16a34a] text-white px-3 h-9 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                    style={{ borderRadius: 8 }}
                  >
                    <Play size={14} weight="fill" /> Activar
                  </button>
                )}
                {m.estado === "activo" && (
                  <button
                    onClick={() => finish(m.id)}
                    className="bg-[#F97316] hover:bg-[#ea580c] text-white px-3 h-9 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                    style={{ borderRadius: 8 }}
                  >
                    <CheckCircle size={14} weight="fill" /> Finalizar
                  </button>
                )}
                {m.estado === "finalizado" && (
                  <button
                    data-testid={`reopen-partido-${m.id}`}
                    onClick={() => reopen(m)}
                    className="bg-[#22C55E] hover:bg-[#16a34a] text-white px-3 h-9 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                    style={{ borderRadius: 8 }}
                    title="Reabre este partido para poder seguir registrando movimientos"
                  >
                    <Play size={14} weight="fill" /> Reabrir partido
                  </button>
                )}
                <button
                  onClick={() => startEdit(m)}
                  className="border border-slate-200 hover:border-[#2563EB] hover:text-[#2563EB] px-3 h-9 flex items-center gap-1.5 text-xs"
                  style={{ borderRadius: 8 }}
                >
                  <PencilSimple size={14} /> Editar
                </button>
                <button
                  onClick={() => remove(m.id)}
                  className="border border-slate-200 hover:border-[#EF4444] hover:text-[#EF4444] px-3 h-9 flex items-center gap-1.5 text-xs"
                  style={{ borderRadius: 8 }}
                  title="Solo se puede eliminar si no tiene accesos ni movimientos"
                >
                  <Trash size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <form
            onSubmit={onSubmit}
            className="bg-white border border-slate-200 w-full max-w-lg p-6 space-y-4"
            style={{ borderRadius: 6 }}
          >
            <h2 className="font-display font-bold text-2xl uppercase">{editing ? "Editar Partido" : "Nuevo Partido"}</h2>
            <Field label="Rival" required>
              <input data-testid="form-rival" required value={form.rival} onChange={(e) => setForm({ ...form, rival: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Competición">
              <select value={form.competicion} onChange={(e) => setForm({ ...form, competicion: e.target.value })} className={inputCls}>
                <option>Liga</option><option>Copa</option><option>Amistoso</option><option>Play-Off</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha" required>
                <input data-testid="form-fecha" type="date" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Hora" required>
                <input type="time" required value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Temporada">
                <SeasonSelector
                  value={form.temporada}
                  onChange={(v) => setForm({ ...form, temporada: v })}
                  includeAll={false}
                  size="md"
                  testid="form-temporada"
                />
              </Field>
              <Field label="Aforo máximo" required>
                <input data-testid="form-aforo" type="number" required value={form.aforo_maximo} onChange={(e) => setForm({ ...form, aforo_maximo: parseInt(e.target.value) || 0 })} className={inputCls} />
              </Field>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 h-11 border border-slate-200" style={{ borderRadius: 8 }}>Cancelar</button>
              <button data-testid="form-save-partido" type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide shadow-md shadow-blue-500/20" style={{ borderRadius: 8 }}>{editing ? "Guardar" : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 px-3 h-10 text-slate-900 focus:border-[#2563EB] outline-none placeholder:text-slate-400";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.2em] uppercase font-bold text-slate-600 mb-1.5">
        {label}{required && <span className="text-[#EF4444]"> *</span>}
      </span>
      {children}
    </label>
  );
}
